(() => {
  if (customElements.get('oilogic-header')) return;

class OilogicHeader extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.bar = this.querySelector('.header__bar');
    this.scheme = this.dataset.scheme;
    this.stickyScheme = this.dataset.stickyScheme;
    this.offset = parseInt(this.dataset.stickyOffset, 10) || 0;
    this.stickyMode = this.dataset.stickyMode || 'always';
    this.lastY = window.scrollY;

    this.setHeightVar();
    window.addEventListener('resize', () => this.setHeightVar(), { passive: true, signal });

    this.panels = new Map();
    this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      this.panels.set(panel.dataset.megaPanel, panel);
    });
    this.triggers = [];

    this.openKey = null;
    this.closeTimer = null;
    this.openTimer = null;
    this.pendingKey = null;
    this.openDelay = parseInt(this.dataset.megaOpenDelay, 10);
    if (Number.isNaN(this.openDelay)) this.openDelay = 150;

    if (this.stickyMode !== 'none') {
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.onScroll();
    }

    this.querySelectorAll('.header__nav-link[data-nav-handle]').forEach((link) => {
      const key = link.dataset.navHandle;
      const panel = this.panels.get(key);
      if (!panel) return;

      link.dataset.megaTrigger = key;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      link.setAttribute('aria-controls', `MegaMenu-${key}`);
      this.triggers.push(link);

      const item = link.closest('.header__nav-item');
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(key);
      }, { signal });
      link.addEventListener('keydown', (event) => {
        if (event.key === ' ') {
          event.preventDefault();
          this.open(key);
        }
      }, { signal });
      link.addEventListener('mouseenter', () => this.scheduleOpen(key), { signal });
      link.addEventListener('focus', () => this.open(key), { signal });

      if (item) {
        item.addEventListener('mouseenter', () => this.cancelClose(), { signal });
        item.addEventListener('mouseleave', () => {
          this.cancelOpen();
          this.scheduleClose();
        }, { signal });
      }
      panel.addEventListener('mouseenter', () => this.cancelClose(), { signal });
      panel.addEventListener('mouseleave', () => this.scheduleClose(), { signal });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.drawerOpen) {
        this.closeDrawer();
      } else if (this.openKey) {
        const trigger = this.triggerFor(this.openKey);
        this.close();
        if (trigger) trigger.focus();
      }
    }, { signal });

    document.addEventListener('click', (event) => {
      if (this.openKey && !this.contains(event.target)) this.close();
    }, { signal });

    this.addEventListener('focusout', () => {
      if (!this.openKey) return;
      requestAnimationFrame(() => {
        if (this.openKey && !this.contains(document.activeElement)) this.close();
      });
    }, { signal });

    this.setupNavPill(signal);
    this.setupDrawer(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    document.body.style.overflow = '';
  }

  triggerFor(key) {
    return this.triggers.find((trigger) => trigger.dataset.megaTrigger === key);
  }

  setHeightVar() {
    if (this.bar) document.documentElement.style.setProperty('--header-height', `${this.bar.offsetHeight}px`);
  }

  onScroll() {
    const y = window.scrollY;
    const stuck = y > this.offset;
    this.classList.toggle('header--sticky', stuck);

    if (this.stickyMode === 'on-scroll-up') {
      if (y <= this.offset) this.classList.remove('header--hidden');
      // Not while a mega panel is open.
      else if (!this.openKey) this.classList.toggle('header--hidden', y > this.lastY);
      this.lastY = y;
    }

    if (this.bar && this.stickyScheme && this.stickyScheme !== this.scheme) {
      const active = stuck ? this.stickyScheme : this.scheme;
      const previous = stuck ? this.scheme : this.stickyScheme;
      this.bar.classList.remove(`color-${previous}`);
      this.bar.classList.add(`color-${active}`);
    }
  }

  scheduleOpen(key) {
    this.cancelClose();
    if (this.openKey === key) return;
    if (this.openKey) return this.open(key);
    if (this.pendingKey === key) return;
    this.cancelOpen();
    this.pendingKey = key;
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.pendingKey = null;
      this.open(key);
    }, this.openDelay);
  }

  cancelOpen() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.pendingKey = null;
  }

  open(key) {
    this.cancelClose();
    this.cancelOpen();
    if (this.openKey === key) return;
    if (this.openKey) this.close();

    const panel = this.panels.get(key);
    const trigger = this.triggerFor(key);
    if (!panel) return;

    panel.hidden = false;
    requestAnimationFrame(() => {
      if (this.openKey === key) panel.classList.add('megamenu--open');
    });
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      this.movePill(trigger, true);
    }
    this.openKey = key;
  }

  close() {
    if (!this.openKey) return;

    const panel = this.panels.get(this.openKey);
    const trigger = this.triggerFor(this.openKey);
    if (panel) {
      panel.classList.remove('megamenu--open');
      panel.hidden = true;
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.openKey = null;
    this.restPill();
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.close(), 180);
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  setupNavPill(signal) {
    this.navList = this.querySelector('.header__nav-list');
    this.navPill = this.navList && this.navList.querySelector('.header__nav-pill');
    if (!this.navPill) return;

    this.activeLink = this.navList.querySelector('.header__nav-link[aria-current="page"]');
    const move = (event) => this.movePill(event.currentTarget, true);
    this.navList.querySelectorAll('.header__nav-link').forEach((link) => {
      link.addEventListener('mouseenter', move, { signal });
      link.addEventListener('focus', move, { signal });
    });
    this.navList.addEventListener('mouseleave', () => this.restPill(), { signal });
    window.addEventListener('resize', () => this.restPill(), { passive: true, signal });
    this.restPill();
  }

  movePill(link, show) {
    if (!this.navPill || !link || link.offsetParent === null) return;
    const style = this.navList.style;
    style.setProperty('--pill-x', `${link.offsetLeft}px`);
    style.setProperty('--pill-y', `${link.offsetTop}px`);
    style.setProperty('--pill-w', `${link.offsetWidth}px`);
    style.setProperty('--pill-h', `${link.offsetHeight}px`);
    if (show) this.navList.classList.add('header__nav-list--pill-on');
  }

  restPill() {
    if (!this.navPill) return;
    const target = this.openKey ? this.triggerFor(this.openKey) : this.activeLink;
    if (target && target.offsetParent !== null) {
      this.movePill(target, true);
    } else if (!this.openKey) {
      this.navList.classList.remove('header__nav-list--pill-on');
    }
  }

  setupDrawer(signal) {
    this.drawer = this.querySelector('[data-drawer]');
    this.backdrop = this.querySelector('[data-drawer-backdrop]');
    this.drawerToggle = this.querySelector('[data-drawer-toggle]');
    this.drawerOpen = false;
    if (!this.drawer || !this.drawerToggle) return;

    this.drawerToggle.addEventListener('click', () => this.toggleDrawer(), { signal });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeDrawer(), { signal });
    }

    this.drawer.addEventListener('keydown', (event) => this.trapFocus(event), { signal });

    // The mobile drill-down (submenu slide-in) lives in <oilogic-drawer-nav>
    // (component-header-drilldown.js); it listens for oilogic:drawer:close to
    // reset its level when the drawer closes.

    // Must match the desktop-nav breakpoint in section-header.css: below it the
    // drawer is the only nav, so crossing it has to close the drawer.
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.desktopQuery.addEventListener('change', (event) => {
      if (event.matches && this.drawerOpen) this.closeDrawer();
    }, { signal });
  }

  toggleDrawer() {
    if (this.drawerOpen) this.closeDrawer();
    else this.openDrawer();
  }

  openDrawer() {
    if (this.drawerOpen) return;
    this.drawerOpen = true;

    this.classList.add('header--drawer-open');
    // Anchor panels to the bar's real bottom edge (the announcement bar offsets
    // it before scroll), so --header-height alone won't tuck them behind it.
    if (this.bar) {
      document.documentElement.style.setProperty('--drawer-anchor', `${Math.round(this.bar.getBoundingClientRect().bottom)}px`);
    }
    if (this.backdrop) this.backdrop.hidden = false;
    this.drawer.hidden = false;
    requestAnimationFrame(() => {
      if (this.backdrop) this.backdrop.classList.add('header__backdrop--open');
      this.drawer.classList.add('header__drawer--open');
    });

    this.drawerToggle.setAttribute('aria-expanded', 'true');
    const closeLabel = this.drawerToggle.dataset.labelClose;
    if (closeLabel) this.drawerToggle.setAttribute('aria-label', closeLabel);
    document.body.style.overflow = 'hidden';

    const focusable = this.drawerFocusables();
    (focusable[0] || this.drawer).focus();
  }

  closeDrawer() {
    if (!this.drawerOpen) return;
    this.drawerOpen = false;

    // Reset the mobile drill-down (handled by <oilogic-drawer-nav>).
    this.dispatchEvent(new CustomEvent('oilogic:drawer:close'));

    this.classList.remove('header--drawer-open');
    this.drawer.classList.remove('header__drawer--open');
    if (this.backdrop) this.backdrop.classList.remove('header__backdrop--open');

    const finish = () => {
      clearTimeout(fallback);
      this.drawer.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.drawer.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.target !== this.drawer || event.propertyName !== 'transform') return;
      finish();
    };
    this.drawer.addEventListener('transitionend', onEnd);
    const fallback = setTimeout(finish, 400);

    document.body.style.overflow = '';
    this.drawerToggle.setAttribute('aria-expanded', 'false');
    const openLabel = this.drawerToggle.dataset.labelOpen;
    if (openLabel) this.drawerToggle.setAttribute('aria-label', openLabel);
    this.drawerToggle.focus();
  }

  drawerFocusables() {
    return Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = this.drawerFocusables();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.drawer.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('oilogic-header', OilogicHeader);
})();
